import type { RouteObject } from 'react-router-dom';

import { type RecordType, type UsageRecord, isRecordId } from '@/usage/events';
import { routeMatch } from '@/usage/scrub';

/**
 * The record a detail page shows, for usage analytics (owner 2026-10-15: "I
 * would like to know which pages were used more"). The page view keeps its
 * route pattern (`/partnerProfile/:propertyId`) and adds `record_type` and
 * `record_id`: the numeric id taken from the route, nothing else (no name, no
 * other parameter, no query string).
 *
 * Route pattern (src/routes) -> the record type and the parameter holding its
 * id. A route not listed here sends no record.
 */
const RECORD_ROUTES: ReadonlyMap<string, { type: RecordType; param: string }> = new Map(
  Object.entries({
    '/brandprofile/:id': { type: 'brand', param: 'id' },
    '/brandprofile/:id/:flag': { type: 'brand', param: 'id' },
    '/partnerProfile/:propertyId': { type: 'partner', param: 'propertyId' },
    '/partnerProfile/:propertyId/:brandId': { type: 'partner', param: 'propertyId' },
    '/viewcontract/:id': { type: 'contract', param: 'id' },
    '/viewActivation/:id': { type: 'activation', param: 'id' },
    '/viewActivity/:id': { type: 'activity', param: 'id' },
    '/comparison/activation/:id': { type: 'activation', param: 'id' },
    '/addActivity/:activationId': { type: 'activation', param: 'activationId' },
    '/productLaunch/:activationId': { type: 'activation', param: 'activationId' },
    '/users/:id': { type: 'user', param: 'id' },
    '/rolepermission/:pagetype/:id': { type: 'role', param: 'id' },
    '/emailtemplates/:id/edit': { type: 'email_template', param: 'id' },
  } satisfies Record<string, { type: RecordType; param: string }>),
);

/** `/addcontract/:idType/:id`: a new contract for a brand or a partner (property). */
const CONTRACT_FOR: ReadonlyMap<string, RecordType> = new Map<string, RecordType>([
  ['brand', 'brand'],
  ['property', 'partner'],
]);

/**
 * A route parameter that is a plain positive number (`"123"`, or already 123), as a number;
 * anything else (a name, a word, a negative or fractional number) null.
 */
export const recordIdOf = (value: unknown): number | null => {
  if (typeof value === 'number') return isRecordId(value) ? value : null;
  if (typeof value !== 'string' || !/^[1-9]\d{0,15}$/.test(value)) return null;
  const id = Number(value);
  return isRecordId(id) ? id : null;
};

/** The record of the page at `pathname`, or null (not a detail page, or its id is not a number). */
export const routeRecord = (
  routes: RouteObject[] | null | undefined,
  pathname: string,
): UsageRecord | null => {
  const match = routeMatch(routes, pathname);
  if (!match) return null;
  if (match.pattern === '/addcontract/:idType/:id') {
    const type = CONTRACT_FOR.get(match.params.idType ?? '');
    const id = recordIdOf(match.params.id);
    return type && id ? { record_type: type, record_id: id } : null;
  }
  const route = RECORD_ROUTES.get(match.pattern);
  if (!route) return null;
  const id = recordIdOf(match.params[route.param]);
  return id ? { record_type: route.type, record_id: id } : null;
};

/** The route patterns that carry a record (for tests and the documentation). */
export const RECORD_ROUTE_PATTERNS: readonly string[] = [
  ...RECORD_ROUTES.keys(),
  '/addcontract/:idType/:id',
];
